// Types
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
// Slices
import {
  checkIfFirstRelationTypeExists,
  copyRelationType,
  createUnsavedRelationType,
  deleteRelationType,
  relationTypeMapSelector,
  relationTypesLoading,
  relationTypesSelector,
  selectedRelationTypeIdsSelector,
  setFirstRelationType,
  setSecondRelationType,
} from 'slices/relationType.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { IProps } from 'types/props.interface';
// Utils
import { matchString } from 'utils/stringUtils';
import { ERelationType, type TRelationType } from '@timeedit/types';
// Components
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
  type TEIOption,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
// Styles
import './RelationTypeList.scss';

interface IRowData {
  id: number;
  name: string;
  type: string;
}

const RelationTypeList = ({ confirmDiscardChanges }: IProps) => {
  const dispatch = useAppDispatch();

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const relationTypes: TRelationType[] = useAppSelector(relationTypesSelector);
  const relationTypeMap: Record<number, TRelationType> = useAppSelector(
    relationTypeMapSelector
  );
  const loading = useAppSelector(relationTypesLoading);
  const selectedRelationTypeIds = useAppSelector(
    selectedRelationTypeIdsSelector
  );
  const [highlightIds, setHighlightIds] = useState<number[]>([]);

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  useEffect(() => {
    setTimeout(() => setHighlightIds(selectedRelationTypeIds), 100);
  }, [selectedRelationTypeIds.join('-')]);

  const sourceData: IRowData[] = relationTypes.map(
    (relationType: TRelationType) => ({
      id: relationType.id,
      name: relationType.name || getText('new_relation_type'),
      type: getText(relationType.relationType),
    })
  );

  const filterSortData = useMemo(() => {
    const {
      filters: { type },
      freeTextSearch,
    } = filterSettings;

    return sourceData.filter((row: IRowData) => {
      let matchType = true;
      let matchFreeText = true;
      if (type !== undefined) {
        matchType = type === row.type;
      }
      if (freeTextSearch) {
        matchFreeText = matchString(row.name, freeTextSearch);
      }
      return matchType && matchFreeText;
    });
  }, [sourceData, filterSettings]);

  const onCreate = useCallback(() => {
    confirmDiscardChanges(true, () => dispatch(createUnsavedRelationType()));
  }, []);

  const onDelete = useCallback(
    (record: IRowData) => {
      confirmDelete(record.name, () => dispatch(deleteRelationType(record.id)));
    },
    [confirmDelete, dispatch]
  );

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardChanges(true, () =>
        dispatch(copyRelationType(relationTypeMap[record.id]))
      );
    },
    [relationTypeMap]
  );

  const onRowClick = useCallback(
    (record: IRowData) => {
      confirmDiscardChanges(true, () =>
        dispatch(setFirstRelationType(relationTypeMap[record.id]))
      );
    },
    [relationTypeMap]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onCompare = useCallback(
    (record: IRowData) => {
      const isFirstRelationTypeExists = dispatch(
        checkIfFirstRelationTypeExists()
      );
      if (!isFirstRelationTypeExists) {
        return dispatch(setFirstRelationType(relationTypeMap[record.id]));
      }
      confirmDiscardChanges(false, () =>
        dispatch(setSecondRelationType(relationTypeMap[record.id]))
      );
    },
    [relationTypeMap]
  );

  const getDrawer = useCallback(
    () => dispatch(checkIfFirstRelationTypeExists()),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen: getDrawer,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, getDrawer]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Button size="small" type="primary" onClick={onCreate}>
          Create new
        </Button>
      </Space>
    ),
    [onCreate]
  );

  const title = useTableTitle<IRowData>({
    total: relationTypes.length,
    filteredAmount: filterSortData.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="field-relation-list" ref={contentRef}>
      <Filter
        settings={filterSettings}
        onChange={setFilterSettings}
        defaultFields={defautlFilters}
        options={options}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterSortData}
          columns={columns}
          getMenuProps={menuProps}
          onRow={onRow}
          title={title}
          footer={footer}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default memo(RelationTypeList);

const columns = [
  {
    title: 'Name',
    key: 'name',
    dataIndex: 'name',
    sorter: (a: IRowData, b: IRowData) => a.name.localeCompare(b.name),
  },
  {
    title: 'Type',
    key: 'type',
    dataIndex: 'type',
    sorter: (a: IRowData, b: IRowData) => a.type.localeCompare(b.type),
  },
];

const defautlFilters: TEIFilterField[] = [
  {
    key: 'type',
    label: 'Type',
    type: TEEFilterType.text,
  },
];
const options: Record<string, TEIOption[]> = {
  type: Object.values(ERelationType)
    .map((type) => ({
      value: getText(type),
      label: getText(type),
    }))
    .sort((option1, option2) => option1.label.localeCompare(option2.label)),
};
